import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { useUserRole } from "@/hooks/useUserRole";
import { EnvironmentSwitcher, useEnvironmentBorderClass } from "./EnvironmentSwitcher";
import { useTenant } from "@/hooks/useTenant";
import { AlertTriangle, Eye } from "lucide-react";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationPanelProvider, useNotificationPanel } from "@/components/notifications/NotificationPanelContext";
import { NotificationPanel } from "@/components/notifications/NotificationPanel";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface AppLayoutProps {
  children: React.ReactNode;
  title: string;
  titleExtra?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  centerActions?: boolean;
}

export function AppLayout({ children, title, titleExtra, subtitle, actions, centerActions = false }: AppLayoutProps) {
  const { roles, isLoading, isAnalyst } = useUserRole();
  const { activeTenant } = useTenant();
  const showUnassignedBanner = !isLoading && roles.length === 0;
  const envBorderClass = useEnvironmentBorderClass();

  return (
    <SidebarProvider>
      <NotificationPanelProvider>
      <AppSidebar />
      <SidebarInset className="h-svh min-w-0">
        {showUnassignedBanner && (
          <div className="shrink-0 bg-destructive text-destructive-foreground px-6 py-3 flex items-center gap-3 font-medium">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span>Your account has not been assigned a role yet. Please contact an administrator to get access.</span>
          </div>
        )}
        {isAnalyst && activeTenant !== "prod" && (
          <div className="shrink-0 bg-muted text-muted-foreground px-6 py-2 flex items-center gap-2 text-sm">
            <Eye className="h-4 w-4 shrink-0" />
            <span>You are viewing this environment as an analyst (read-only)</span>
          </div>
        )}
        <header className={`shrink-0 flex flex-col border-b bg-card px-6 ${envBorderClass} ${subtitle ? 'pt-3 gap-3' : 'justify-center h-16'}`}>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 shrink-0">
              <SidebarTrigger className="md:hidden -ml-2" />
              {title && <h1 className="text-xl font-semibold text-foreground">{title}</h1>}
              {titleExtra}
            </div>
            <EnvironmentSwitcher />
            <div className={centerActions ? "flex-1 flex justify-center ml-auto" : "ml-auto flex items-center gap-2"}>
              {actions && <div className="flex items-center gap-2">{actions}</div>}
              <NotificationBell />
            </div>
          </div>
          {subtitle && <div className="flex items-center">{subtitle}</div>}
        </header>
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-6 bg-background">
          {children}
        </main>
      </SidebarInset>
      <NotificationSidePanel />
      </NotificationPanelProvider>
    </SidebarProvider>
  );
}

function NotificationSidePanel() {
  const { open, setOpen } = useNotificationPanel();
  if (!open) return null;
  return (
    <aside className="h-svh shrink-0 w-full sm:w-[420px] border-l bg-card flex flex-col">
      <div className="shrink-0 flex items-center justify-between px-4 h-14 border-b">
        <h2 className="text-base font-semibold">Notifications</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(false)}
          aria-label="Close notifications"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <NotificationPanel onClose={() => setOpen(false)} />
    </aside>
  );
}
